import {emotionStyled} from '@packages/utilities/styled';
import {FloatingArrow} from '@floating-ui/react';
import {tokens} from '../../themes';

export const TooltipWrapperView = emotionStyled('span')(
  {
    background: '$backgroundTertiary',
    boxShadow: '$menu',
    color: '$actionPrimaryText',
    padding: tokens.ui.tooltip.padding.comfortable,
    borderRadius: '$2',
    transitionProperty: 'opacity,transform',
    /* display: flex fixes a line height issue where there was excess space at the top of the tooltip */
    display: 'flex',
    minHeight: 'fit-content',
  },
  {
    componentName: 'TooltipWrapper',
    variants: {
      colorVariant: {
        default: {
          backgroundColor: '$backgroundTertiary',
          color: '$textPrimary',
          '.fill': {
            fill: '$backgroundTertiary',
          },
        },
        danger: {
          backgroundColor: '$redBackground',
          '.fill': {
            fill: '$redBackground',
          },
        },
        primary: {
          backgroundColor: '$actionPrimaryBackground',
          '.fill': {
            fill: '$actionPrimaryBackground',
          },
        },
        success: {
          backgroundColor: '$greenBackground',
          '.fill': {
            fill: '$greenBackground',
          },
        },
        warning: {
          backgroundColor: '$yellowBackground',
          '.fill': {
            fill: '$yellowBackground',
          },
        },
        neutralPrimary: {
          backgroundColor: '$backgroundPrimary',
          color: '$textPrimary',
          '.fill': {
            fill: '$backgroundPrimary',
          },
        },
      },
      density: {
        comfortable: {},
        compact: {
          padding: tokens.ui.tooltip.padding.compact,
        },
      },
      lightModeShadowPlacement: {
        top: {
          filter: `
          drop-shadow(0px 1px 0px rgba(0, 0, 0, 0.04))
          drop-shadow(0px 2px 1px rgba(0, 0, 0, 0.02))
        `,
        },
        bottom: {
          /* The rgba is slightly darker here because our light-source is "above" us*/
          filter: `
            drop-shadow(0px -1px 0px rgba(0, 0, 0, 0.08))
            drop-shadow(0px -2px 1px rgba(0, 0, 0, 0.02))
          `,
        },
        left: {
          filter: `
          drop-shadow(1px 0px 0px rgba(0, 0, 0, 0.04))
          drop-shadow(1px 2px 1px rgba(0, 0, 0, 0.02))
        `,
        },
        right: {
          filter: `
          drop-shadow(-1px 0px 0px rgba(0, 0, 0, 0.04))
          drop-shadow(-1px 2px 1px rgba(0, 0, 0, 0.02))
        `,
        },
      },
      placement: {
        top: {},
        'top-start': {},
        'top-end': {},
        bottom: {},
        'bottom-start': {},
        'bottom-end': {},
        left: {},
        'left-start': {},
        'left-end': {},
        right: {},
        'right-start': {},
        'right-end': {},
      },
      variant: {
        default: {},
        commentBubble: {
          borderRadius: '$6',
          padding: tokens.ui.tooltip.padding.commentBubble,
        },
      },
    },
    compoundVariants: [
      {
        conditions: {
          placement: 'left-start',
          variant: 'commentBubble',
        },
        css: {
          borderRadius: '$6 $2 $6 $6',
        },
      },
      {
        conditions: {
          placement: 'right-start',
          variant: 'commentBubble',
        },
        css: {
          borderRadius: '$2 $6 $6 $6',
        },
      },
      {
        conditions: {
          placement: 'left-end',
          variant: 'commentBubble',
        },
        css: {
          borderRadius: '$6 $6 $2 $6',
        },
      },
      {
        conditions: {
          placement: 'right-end',
          variant: 'commentBubble',
        },
        css: {
          borderRadius: '$6 $6 $6 $2',
        },
      },
    ],
    defaultVariants: {
      colorVariant: 'default',
    },
  }
);

export const TooltipContentView = emotionStyled('span')(
  {
    fontSize: '$body',
    /* Hyphenate our Tooltip content in the case where a maxWidth is not enough to hold the text contents. */
    hyphens: 'auto',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
  },
  {
    componentName: 'TooltipContent',
  }
);

export const TooltipArrow = emotionStyled(FloatingArrow)(
  {},
  {
    componentName: 'TooltipArrow',
    variants: {
      colorVariant: {
        default: {
          fill: '$backgroundTertiary',
        },
        danger: {
          fill: '$redBackground',
        },
        primary: {
          fill: '$actionPrimaryBackground',
        },
        success: {
          fill: '$greenBackground',
        },
        warning: {
          fill: '$yellowBackground',
        },
        neutralPrimary: {
          fill: '$backgroundPrimary',
        },
      },
    },
    /**
     * We need to remove colorVariant from the props we pass through to `FloatingArrow`.
     *
     * It passes any props it receives down to the underlying `svg` element, causing a DOM error.
     */
    filterForwardedProps: ({colorVariant: _, ...rest}) => rest,
    defaultVariants: {
      colorVariant: 'default',
    },
  }
);

export const TooltipArrowShadow = emotionStyled(TooltipArrow)(
  {
    filter: `drop-shadow(0px 12px 24px rgba(0, 0, 0, 0.08))
      drop-shadow(0px 8px 16px rgba(0, 0, 0, 0.08))
      drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.08))
      drop-shadow(0px 2px 6px rgba(0, 0, 0, 0.08))
      drop-shadow(0px -0.5px 0.5px rgba(0, 0, 0, 0.12))
      drop-shadow(0px 0.5px 0.5px rgba(255, 255, 255, 0.12))`,
  },
  {
    componentName: 'TooltipArrowShadow',
    variants: {
      placement: {
        top: {
          filter: `drop-shadow(0px 12px 24px rgba(0, 0, 0, 0.08))
            drop-shadow(0px 8px 16px rgba(0, 0, 0, 0.08))
            drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.08))
            drop-shadow(0px 2px 6px rgba(0, 0, 0, 0.08))`,
        },
        bottom: {},
        left: {
          filter: `
            drop-shadow(1px 0.5px 0.5px rgba(255, 255, 255, 0.12))`,
        },
        right: {
          filter: `
          drop-shadow(-1px 0.5px 0.5px rgba(255, 255, 255, 0.12))`,
        },
      },
    },
  }
);

/**
 * We are defining a custom title here instead of using `Text`, because our `Text` component relies on `Tooltip`.
 * This causes a circular dependency.
 */
export const TooltipTitle = emotionStyled('div')(
  {
    color: '$textPrimary',
    fontFamily: '$body',
    fontSize: '$body',
    fontWeight: '$semibold',
    lineHeight: '$body',
    /**
     * We're using marginBottom instead of putting this in an HStack so we don't negatively impact existing tooltips with custom content.
     */
    marginBottom: '$1',
  },
  {
    componentName: 'TooltipTitle',
  }
);
